import React, { useEffect, useState } from 'react';
import styles from '../styles/TripAdvisorButton.module.css';

export interface TripAdvisorReview {
  id: string;
  title: string | null;
  text: string;
  rating: number;
  reviewerName: string;
  reviewUrl: string | null;
  ratingIconUrl: string | null;
}

export interface TripAdvisorResponse {
  reviews: TripAdvisorReview[];
  overallRating: number | null;
  overallRatingIconUrl: string | null;
  tripadvisorUrl: string;
  writeReviewUrl: string | null;
}

interface TripAdvisorButtonProps {
  locationId: string;
}

const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';

const isNullableRating = (value: unknown): value is number | null =>
  value === null || (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= 1 &&
    value <= 5
  );

const isReview = (value: unknown): value is TripAdvisorReview => {
  if (!value || typeof value !== 'object') return false;
  const review = value as Record<string, unknown>;

  return (
    typeof review.id === 'string' &&
    isNullableString(review.title) &&
    typeof review.text === 'string' &&
    typeof review.rating === 'number' &&
    Number.isFinite(review.rating) &&
    typeof review.reviewerName === 'string' &&
    isNullableString(review.reviewUrl) &&
    isNullableString(review.ratingIconUrl)
  );
};

const isTripAdvisorResponse = (value: unknown): value is TripAdvisorResponse => {
  if (!value || typeof value !== 'object') return false;
  const payload = value as Record<string, unknown>;

  return (
    Array.isArray(payload.reviews) &&
    payload.reviews.every(isReview) &&
    isNullableRating(payload.overallRating) &&
    isNullableString(payload.overallRatingIconUrl) &&
    typeof payload.tripadvisorUrl === 'string' &&
    isNullableString(payload.writeReviewUrl)
  );
};

const getRatingLabel = (rating: number) => {
  if (rating >= 4.5) return 'Excellent';
  if (rating >= 3.5) return 'Very good';
  if (rating >= 2.5) return 'Average';
  if (rating >= 1.5) return 'Poor';
  return 'Terrible';
};

const TripAdvisorLogo = () => (
  <img
    src="https://static.tacdn.com/img2/brand_refresh/Tripadvisor_lockup_horizontal_secondary_registered.svg"
    alt="Tripadvisor"
    className={styles.taLogo}
  />
);

const BubbleRating = ({
  rating,
  iconUrl,
  size = 'small'
}: {
  rating: number;
  iconUrl: string | null;
  size?: 'small' | 'large';
}) => {
  if (!iconUrl) {
    return (
      <span
        className={`${styles.numericRating} ${size === 'large' ? styles.numericRatingLarge : ''}`}
        aria-label={`${rating} out of 5`}
      >
        {rating.toFixed(1)} / 5
      </span>
    );
  }

  return (
    <img
      src={iconUrl}
      alt={`${rating} out of 5`}
      className={size === 'large' ? styles.largeRatingStars : styles.ratingStars}
    />
  );
};

export const TripAdvisorButton: React.FC<TripAdvisorButtonProps> = ({ locationId }) => {
  const [data, setData] = useState<TripAdvisorResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const fallbackUrl = `https://www.tripadvisor.com/Attraction_Review-g1-d${locationId}-Reviews.html`;
  const reviews = data?.reviews ?? [];
  const currentReview = reviews[currentIndex];
  const tripadvisorUrl = data?.tripadvisorUrl ?? fallbackUrl;
  const actionUrl = data?.writeReviewUrl ?? tripadvisorUrl;
  const actionLabel = data?.writeReviewUrl ? 'Write a review' : 'View on Tripadvisor';

  useEffect(() => {
    const controller = new AbortController();
    const fetchUrl = `/get-tripadvisor-reviews?locationId=${encodeURIComponent(locationId)}`;

    setData(null);
    setCurrentIndex(0);
    setError(false);
    setLoading(true);

    const loadReviews = async () => {
      try {
        const response = await fetch(fetchUrl, { signal: controller.signal });
        if (!response.ok) throw new Error('Failed to fetch Tripadvisor reviews');

        const payload: unknown = await response.json();
        if (!isTripAdvisorResponse(payload)) {
          throw new Error('Tripadvisor returned an unexpected response');
        }

        setData(payload);
      } catch (caughtError) {
        if (controller.signal.aborted) return;
        console.error('Tripadvisor Error:', caughtError);
        setError(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    void loadReviews();
    return () => controller.abort();
  }, [locationId]);

  useEffect(() => {
    if (reviews.length <= 1) return;

    const timer = window.setInterval(() => {
      setCurrentIndex((previous) => (previous + 1) % reviews.length);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [reviews.length]);

  if (loading) {
    return (
      <div className={styles.tripadvisorContent}>
        <TripAdvisorLogo />
        <div className={styles.tripadvisorRatingInfo}>
          <span className={styles.reviewCount}>Loading reviews...</span>
        </div>
      </div>
    );
  }

  if (error || !data || !currentReview) {
    return (
      <div className={styles.tripAdvisorContainer}>
        <div className={styles.headerSection}>
          <div className={styles.logoAndButton}>
            <TripAdvisorLogo />
            <a
              href={actionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.tripAdvisorButton}
            >
              <span>{actionLabel}</span>
            </a>
          </div>
          <div className={styles.ratingSummary}>
            {data?.overallRating != null && (
              <>
                <BubbleRating
                  rating={data.overallRating}
                  iconUrl={data.overallRatingIconUrl}
                  size="large"
                />
                <span className={styles.summaryText}>
                  {`${data.overallRating} - ${getRatingLabel(data.overallRating)}`}
                </span>
              </>
            )}
            <span className={styles.summaryText}>
              {error ? 'Unable to load Tripadvisor reviews' : 'No reviews available'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  const reviewerInitial = currentReview.reviewerName.charAt(0).toUpperCase() || '?';
  const reviewUrl = currentReview.reviewUrl ?? tripadvisorUrl;

  return (
    <div className={styles.tripAdvisorContainer}>
      <div className={styles.headerSection}>
        <div className={styles.logoAndButton}>
          <TripAdvisorLogo />
          <a
            href={actionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.tripAdvisorButton}
          >
            <span>{actionLabel}</span>
          </a>
        </div>
        {data.overallRating !== null && (
          <div className={styles.ratingSummary}>
            <BubbleRating
              rating={data.overallRating}
              iconUrl={data.overallRatingIconUrl}
              size="large"
            />
            <span className={styles.summaryText}>
              {`${data.overallRating} - ${getRatingLabel(data.overallRating)}`}
            </span>
          </div>
        )}
      </div>

      <div className={styles.reviewsCarouselSection}>
        <div className={styles.reviewSlide} key={currentReview.id}>
          <div className={styles.cardHeader}>
            <BubbleRating
              rating={currentReview.rating}
              iconUrl={currentReview.ratingIconUrl}
            />
          </div>
          {currentReview.title && (
            <h3 className={styles.reviewTitle}>{currentReview.title}</h3>
          )}
          <p className={styles.reviewText}>&ldquo;{currentReview.text}&rdquo;</p>
          <div className={styles.reviewLinkRow}>
            <a
              href={reviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.tripadvisorTextLink}
            >
              Read more
            </a>
          </div>
          <div className={styles.reviewFooter}>
            <div className={styles.authorInitial} aria-hidden="true">
              {reviewerInitial}
            </div>
            <div className={styles.authorInfo}>
              <span className={styles.reviewAuthor}>{currentReview.reviewerName}</span>
            </div>
            <a
              href={tripadvisorUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.moreOnTripadvisor}
            >
              More on Tripadvisor
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
